import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import { InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import { BodyCell, DataGrid, IndexBasedGridSelection, SelectionBehavior } from '@tableau/widgets-datagrid';
import React from 'react';
import { FunctionComponent } from 'react';
import { defaultMemoize } from 'reselect';
import { style } from 'typestyle';

import { CommonStyles } from '~common/react/CommonStyles';
import { createRowStore, defaultRowStructure } from '~common/react/components/dataGrid/SortableDataGrid3';
import { SortableHeader } from '~common/react/components/dataGrid/SortableHeader';
import { ReactActionDialogBodyProps } from '~commonInfra/dialogs/ReactActionDialog';
import { Messages } from '~src/codegen/Localize';
import {
  PublisherNameFormatter,
  PublishTimeFormatter,
  RevisionHistoryActionCellFormatter,
  RevisionNameFormatter, RevisionNameFormatterProps,
} from '~src/revisionHistory/components/RevisionHistoryCellFormatters';
import {
  revisionHistoryDialogGridContainerStyle,
  revisionHistoryDialogPromptStyle,
} from '~src/revisionHistory/components/styles/DialogStyles';
import { IContentRevision } from '~src/revisionHistory/serverApi/types/IContentRevision';
import { IQueryUpdater, QueryServiceInstance } from '~src/revisionHistory/services/QueryService';
import { DatasourceRevisionsInstance } from '~src/revisionHistoryDatasource/resources/DatasourceRevisions';
import { FlowRevisions } from '~src/revisionHistoryFlow/resources/FlowRevisions';
import { PublishedConnectionRevisions } from '~src/revisionHistoryPublishedConnection/resources/PublishedConnectionRevisions';
import { WorkbookRevisionsInstance } from '~src/revisionHistoryWorkbook/resources/WorkbookRevisions';
import { ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import {
  DatasourceContentType,
  FlowContentType,
  IContentType,
  PublishedConnectionContentType,
  RevisionContentType,
  WorkbookContentType,
} from '~src/spacesPlatform/models/ContentTypes';
import DataGridRowHeight = CommonStyles.Sizes.DataGridRowHeight;
import CellSelectionEvent = DataGrid.CellSelectionEvent;

export interface RevisionHistoryDialogBodyProps {
  dialogDescription: string;
  contentType: IContentType;
  contentItemId: string;
}

export interface RevisionHistoryDialogBodyState {
  selectedItem: IContentRevision;
  errorMessage?: string;
  // deletedItem is used for conditionally re-rendering the dialog body
  // the dialog body would re-render when the restore button on confirmation dialog is enabled from dialog body
  // the rerender deselects the current selection in the grid so the body is memoized; but we do want to re-render when
  // an item is deleted which happens outside the dialog body; so this state variable helps with conditional re-render
  deletedItem: IContentRevision;
}

export type RevisionHistoryDialogBodyStateAndProps = ReactActionDialogBodyProps<RevisionHistoryDialogBodyProps, RevisionHistoryDialogBodyState>;

const ACTION_COLUMN_KEY = 'actions';

function revisionNameCellFormatter(contentType: IContentType,
                                   unselectableRevisionRowKeys: Set<string>): FunctionComponent<BodyCell.Props> {
  return (props: BodyCell.Props) => {
    const revisionNameFormatterProps: RevisionNameFormatterProps = {
      bodyCellProps: props,
      contentType: contentType,
      onCurrentRevision: rowKey => unselectableRevisionRowKeys.add(rowKey),
      onDeletedRevision: rowKey => unselectableRevisionRowKeys.add(rowKey)
    };
    return <RevisionNameFormatter {...revisionNameFormatterProps} />;
  };
}

function getDataGridColumns(props: RevisionHistoryDialogBodyPropsAndState,
                            unselectableRevisionRowKeys: Set<string>): DataGrid.Column[] {
  const columns: DataGrid.Column[] = [];
  columns.push({
    name: 'version-num-col',
    dataKey: 'versionNumber',
    headerText: Messages.revisionHistoryRevisionNumberColumn(),
    headerFormatter: SortableHeader.Formatter(),
    cellFormatter: revisionNameCellFormatter(props.contentType, unselectableRevisionRowKeys),
    resizable: false,
    growToFit: true,
    sortable: false,
  });

  columns.push({
    name: 'publisher-col',
    dataKey: 'publisher',
    headerText: Messages.revisionHistoryPublisherColumn(),
    headerFormatter: SortableHeader.Formatter(),
    cellFormatter: bodyCellProps =>  <PublisherNameFormatter {...bodyCellProps}/>,
    resizable: false,
    growToFit: true,
    sortable: false,
  });

  columns.push({
    name: 'publish-time-col',
    dataKey: 'publishTime',
    headerText: Messages.revisionHistoryPublishDateColumn(),
    headerFormatter: SortableHeader.Formatter(),
    cellFormatter: bodyCellProps => <PublishTimeFormatter {...bodyCellProps}/>,
    resizable: false,
    growToFit: true,
    sortable: false,
  });

  columns.push({
    name: 'actions-col',
    dataKey: ACTION_COLUMN_KEY,
    headerText: Messages.actions(),
    headerFormatter: SortableHeader.Formatter(),
    cellFormatter: bodyCellProps => <RevisionHistoryActionCellFormatter {...bodyCellProps}
                                                                        contentType={props.contentType}
                                                                        updateDialogState={props.updateDialogState}/>,
    resizable: false,
    sortable: false,
  });

  return columns;
}

function createQueryUpdater(contentType: IContentType,
                            contentItemId: string): IQueryUpdater {
  let resource;
  let filterKey;
  switch (contentType.name) {
    case WorkbookContentType.name:
      resource = WorkbookRevisionsInstance;
      filterKey = ContentFiltersKeys.workbook;
      break;
    case DatasourceContentType.name:
      resource = DatasourceRevisionsInstance;
      filterKey = ContentFiltersKeys.datasource;
      break;
    case FlowContentType.name:
      resource = FlowRevisions;
      filterKey = ContentFiltersKeys.flow;
      break;
    case PublishedConnectionContentType.name:
      resource = PublishedConnectionRevisions;
      filterKey = ContentFiltersKeys.virtualConnection;
      break;
    default:
      throw new Error(Messages.revisionHistoryUnsupportedContentType());
  }

  const filter: any = {};
  filter[filterKey] = contentItemId;

  return QueryServiceInstance.createQueryUpdater<IContentRevision>(
    RevisionContentType,
    resource,
    resourceQuery => resourceQuery,
    filter,
  );
}

type RevisionHistoryDialogBodyPropsAndState = RevisionHistoryDialogBodyProps & {
  readonly actionBusy: boolean;
  readonly dialogState: RevisionHistoryDialogBodyState;
  readonly updateDialogState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>;
};

// export for use in unit tests
export const ACTION_DIALOG_DESCRIPTION_DIV_ID='action-dialog-description-id';
export const ACTION_DIALOG_DATA_GRID_ID='action-dialog-data-grid-id';
export const ACTION_DIALOG_BODY_ERROR_ID='action-dialog-body-error-id';

// export for unit testing purpose
function getRevisionHistoryDataGridProps(props: RevisionHistoryDialogBodyPropsAndState) {
  const unselectableRevisionRowKeys: Set<string> = new Set<string>();

  const sliceable = createQueryUpdater(props.contentType, props.contentItemId).refresh();
  const rowStore = createRowStore(() => sliceable, defaultRowStructure, []);
  let rows: DataGrid.Row[];
  const cols = getDataGridColumns(props, unselectableRevisionRowKeys);

  const selectionModel = new IndexBasedGridSelection(1, cols.length);
  selectionModel.selectionBehavior = SelectionBehavior.SelectRow;
  selectionModel.selectColumnOnHeaderClick = false;

  function clearSelection(event: DataGrid.CellSelectionEvent) {
    return (event.type === 'mousedown' && selectionModel.isSelected(event.cell) && event.cell.colKey !== ACTION_COLUMN_KEY);
  }

  function handleSelectionEvent(event: CellSelectionEvent) {
    if(!rows){
      return false;
    }

    if (clearSelection(event)) {
      selectionModel.clearSelection();
      props.updateDialogState(prevState => {
        return {...prevState, selectedItem: null, errorMessage: null};
      });
      return true;
    } else if(!unselectableRevisionRowKeys.has(event.cell.rowKey)) {
      selectionModel.exclusiveRowSelect(event.cell.rowIdx);
      const newSelectedItem = rows[event.cell.rowIdx].data as IContentRevision;
      props.updateDialogState(prevState => {
        return {...prevState, selectedItem: newSelectedItem, errorMessage: null};
      });
      return true;
    }

    return false;
  }

  function handleUpdatedRows(updatedRows: DataGrid.Row[]) {
    rows = updatedRows;
  }

  const dataGridProps: DataGrid.Props = {
    cols,
    selectionModel,
    rowStore,
    updated: handleUpdatedRows,
    headerHeight: DataGridRowHeight,
    onSelectionEvent: handleSelectionEvent,
    showRowBanding: false,
    showHorizontalGridLines: true,
    style: {overflow: 'unset', padding: '0px', zIndex: 'auto', height: '190px'},
  };
  return dataGridProps;
}

const getDataGridPropsMemoized = defaultMemoize(getRevisionHistoryDataGridProps);

export const RevisionHistoryDataGrid = React.memo((props: RevisionHistoryDialogBodyStateAndProps): React.ReactElement => {
  const dataGridProps = getDataGridPropsMemoized(props);
  return <div className={style(revisionHistoryDialogGridContainerStyle)}>
    <DataGrid id={ACTION_DIALOG_DATA_GRID_ID} {...dataGridProps}/>
  </div>;
},(prev, next) => prev.contentItemId === next.contentItemId && prev.dialogState.deletedItem === next.dialogState.deletedItem);

export const RevisionHistoryDialogBody = (props: RevisionHistoryDialogBodyStateAndProps) => {
  return <>
    <div id={ACTION_DIALOG_DESCRIPTION_DIV_ID} style={revisionHistoryDialogPromptStyle}>
      {props.dialogDescription}
    </div>
    {<RevisionHistoryDataGrid {...props}></RevisionHistoryDataGrid>}
    {!!props.dialogState.errorMessage && <div id={ACTION_DIALOG_BODY_ERROR_ID}>
      <InlineAlertWidget testId={'revision-action-dialog-body-error-id'} type={InlineAlertType.Error}>
        {props.dialogState.errorMessage}
      </InlineAlertWidget>
      </div>
    }
  </>;
};
